import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Fade, LinearProgress } from '@mui/material'
import TableCell from '@mui/material/TableCell'
import React from 'react'
import {
    useGetValueMessageLinkedDataQuery,
    ValueMessageLinkedData,
} from '../../../../shared/queries/useGetValueMessageLinkedDataQuery'
import { EmptyPlaceholder } from '../../../../shared/components/EmptyPlaceholder'

import SupportingEvidenceExpandedRow from './variants/SupportingEvidenceExpandedRow/SupportingEvidenceExpandedRow'
import { ValueMessage } from '../../../ValueNarrativesView/queries/useGetValueNarrativesQuery'
import OngoingActivitiesExpandedRow from './variants/OngoingActivitiesExpandedRow'

type ValueMessageExpandableRowProps = {
    valueMessageId: string
    isExpanded: boolean
    expandedRowType: keyof Omit<ValueMessageLinkedData, '_id'>
    colorBySubpopulation: string
    navigationStack: ArrayLike<object>
    valueMessage: ValueMessage
}

const Bulletpoint = () => <li style={{ width: 0, marginTop: '5px', fontSize: '12px' }} />

const ValueMessageExpandableRow = ({
    isExpanded,
    expandedRowType,
    valueMessageId,
    colorBySubpopulation,
    navigationStack,
    valueMessage,
}: ValueMessageExpandableRowProps) => {
    const { data, isLoading } = useGetValueMessageLinkedDataQuery(isExpanded ? valueMessageId : null)

    return (
        <TableCell colSpan={1000} padding="none">
            {isLoading && (
                <Stack color={colorBySubpopulation}>
                    <LinearProgress color="inherit" />
                </Stack>
            )}
            {!isLoading && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {expandedRowType === 'supportingEvidence' && (
                        <SupportingEvidenceExpandedRow
                            valueMessage={valueMessage}
                            navigationStack={navigationStack}
                            supportingEvidence={data?.supportingEvidence}
                            keyEvidenceStatements={data?.keyEvidenceStatements}
                        />
                    )}

                    {expandedRowType === 'evidenceGaps' && (
                        <Fade in timeout={800}>
                            <Stack
                                direction="column"
                                pl={9}
                                pr={4}
                                py={1.5}
                                borderTop="1px solid"
                                borderBottom="1px solid"
                                borderColor="figma.grayscale.stroke"
                            >
                                <Typography variant="16x700" mb={0.5}>
                                    Evidence Gaps
                                </Typography>
                                {!data?.evidenceGaps?.length && (
                                    <Stack direction="column" alignItems="flex-start">
                                        <EmptyPlaceholder text="No data to display" />
                                    </Stack>
                                )}
                                {data?.evidenceGaps.map((evidenceGap, index) => (
                                    <Stack
                                        key={evidenceGap._id}
                                        direction="row"
                                        gap={2}
                                        py={1.5}
                                        {...(index !== data.evidenceGaps.length - 1 && {
                                            borderBottom: '1px solid',
                                            borderColor: 'figma.grayscale.stroke',
                                            px: 1.5,
                                            mx: -1.5,
                                        })}
                                    >
                                        <Bulletpoint />
                                        <Typography
                                            variant="16x400"
                                            color="figma.grayscale.textHier2"
                                            lineHeight="26px"
                                        >
                                            {evidenceGap.text}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Fade>
                    )}

                    {expandedRowType === 'ongoingActivities' && (
                        <OngoingActivitiesExpandedRow
                            valueMessage={valueMessage}
                            navigationStack={navigationStack}
                            ongoingActivities={data?.ongoingActivities}
                        />
                    )}
                </Collapse>
            )}
        </TableCell>
    )
}

export default ValueMessageExpandableRow
